import { useState, useMemo } from "react";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Search,
  Wallet,
  Receipt,
  Camera,
  Euro,
  Edit,
  Trash2,
  Scan,
  CheckCircle2,
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/billingUtils";
import {
  EXPENSE_CATEGORY_LABELS,
  EXPENSE_STATUS_LABELS,
  EXPENSE_STATUS_COLORS,
} from "@/types/expenses";
import type {
  Expense,
  ExpenseFormData,
  ExpenseCategory,
  ExpensePaymentStatus,
} from "@/types/expenses";
import { OCRProcessor } from "@/components/Expenses/OCRProcessor";

const emptyExpense: ExpenseFormData = {
  supplier_id: '',
  date: new Date().toISOString().split('T')[0],
  supplier_name: '',
  supplier_tax_id: '',
  concept: '',
  category: 'other',
  base_amount: 0,
  tax_rate: 21,
  invoice_number: '',
  payment_status: 'pending',
  payment_method: 'transfer',
  notes: '',
};

export default function Expenses() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [monthFilter, setMonthFilter] = useState(new Date().toISOString().slice(0, 7));

  const [dialogOpen, setDialogOpen] = useState(false);
  const [ocrDialogOpen, setOcrDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<ExpenseFormData>(emptyExpense);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Fetch expenses
  const { data: expenses = [], isLoading } = useQuery<Expense[]>({
    queryKey: ["expenses", statusFilter, categoryFilter, monthFilter],
    queryFn: async () => {
      let query = api
        .from("expenses")
        .select("*")
        .order("date", { ascending: false });

      if (statusFilter !== "all") {
        query = query.eq("payment_status", statusFilter);
      }

      if (categoryFilter !== "all") {
        query = query.eq("category", categoryFilter);
      }

      if (monthFilter) {
        const [year, month] = monthFilter.split('-');
        const startDate = `${year}-${month}-01`;
        const endDate = new Date(parseInt(year), parseInt(month), 0).toISOString().split('T')[0];
        query = query.gte("date", startDate).lte("date", endDate);
      }

      const { data, error } = await query.limit(500);
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch suppliers for selector
  const { data: suppliers = [] } = useQuery({
    queryKey: ["suppliers-for-expenses"],
    queryFn: async () => {
      const { data, error } = await api
        .from("billing_customers")
        .select("id, legal_name, tax_id, default_expense_account_id")
        .in("entity_type", ["supplier", "both"])
        .eq("is_active", true)
        .order("legal_name");
      if (error) throw error;
      return data || [];
    },
  });

  // Filter by search
  const filteredExpenses = useMemo(() => {
    if (!searchTerm) return expenses;
    const term = searchTerm.toLowerCase();
    return expenses.filter(e =>
      e.supplier_name.toLowerCase().includes(term) ||
      e.concept.toLowerCase().includes(term) ||
      e.invoice_number?.toLowerCase().includes(term) ||
      e.expense_number?.toLowerCase().includes(term)
    );
  }, [expenses, searchTerm]);

  // Stats
  const stats = useMemo(() => {
    const total = expenses.length;
    const thisMonth = expenses.reduce((sum, e) => sum + (e.total_amount || 0), 0);
    const pending = expenses
      .filter(e => e.payment_status === 'pending')
      .reduce((sum, e) => sum + (e.total_amount || 0), 0);
    const ocrCount = expenses.filter(e => e.ocr_processed).length;
    return { total, thisMonth, pending, ocrCount };
  }, [expenses]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!editingExpense.supplier_name || !editingExpense.concept) {
        throw new Error("Proveedor y concepto son obligatorios");
      }

      const taxAmount = Math.round(editingExpense.base_amount * editingExpense.tax_rate / 100 * 100) / 100;
      const totalAmount = editingExpense.base_amount + taxAmount;

      const data = {
        ...editingExpense,
        tax_amount: taxAmount,
        total_amount: totalAmount,
        paid_amount: editingExpense.payment_status === 'paid' ? totalAmount : 0,
        updated_at: new Date().toISOString(),
      };

      if (editingId) {
        const { error } = await api
          .from("expenses")
          .update(data)
          .eq("id", editingId);
        if (error) throw error;
      } else {
        const { data: newExpense, error } = await api
          .from("expenses")
          .insert({ ...data, created_by: user?.id })
          .select("id")
          .single();
        if (error) throw error;

        // Crear asiento contable automaticamente
        if (newExpense?.id) {
          await api.rpc("create_expense_journal_entry", { p_expense_id: newExpense.id });
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      setDialogOpen(false);
      setEditingExpense(emptyExpense);
      setEditingId(null);
      toast.success(editingId ? "Gasto actualizado" : "Gasto registrado");
    },
    onError: (e: any) => toast.error(e.message),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.from("expenses").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      toast.success("Gasto eliminado");
    },
    onError: (e: any) => toast.error(e.message),
  });

  // Mark as paid
  const markPaidMutation = useMutation({
    mutationFn: async (expense: Expense) => {
      const { error } = await api
        .from("expenses")
        .update({
          payment_status: 'paid',
          paid_amount: expense.total_amount,
          payment_date: new Date().toISOString().split('T')[0],
          updated_at: new Date().toISOString(),
        })
        .eq("id", expense.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      toast.success("Marcado como pagado");
    },
    onError: (e: any) => toast.error(e.message),
  });

  const openNewDialog = () => {
    setEditingExpense(emptyExpense);
    setEditingId(null);
    setDialogOpen(true);
  };

  const openEditDialog = (expense: Expense) => {
    setEditingExpense({
      date: expense.date,
      supplier_id: expense.supplier_id,
      supplier_name: expense.supplier_name,
      supplier_tax_id: expense.supplier_tax_id || '',
      concept: expense.concept,
      category: expense.category as ExpenseCategory || 'other',
      base_amount: expense.base_amount,
      tax_rate: expense.tax_rate,
      invoice_number: expense.invoice_number || '',
      payment_status: expense.payment_status,
      payment_method: expense.payment_method || 'transfer',
      notes: expense.notes || '',
    });
    setEditingId(expense.id);
    setDialogOpen(true);
  };

  const handleOCRResult = (data: any) => {
    setEditingExpense({
      ...emptyExpense,
      supplier_name: data.supplier_name || '',
      supplier_tax_id: data.supplier_tax_id || '',
      date: data.date || new Date().toISOString().split('T')[0],
      base_amount: data.base_amount || 0,
      tax_rate: data.tax_rate || 21,
      invoice_number: data.invoice_number || '',
      concept: 'Gasto escaneado por OCR',
    });
    setEditingId(null);
    setOcrDialogOpen(false);
    setDialogOpen(true);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Gastos</h1>
            <p className="text-muted-foreground">
              Registro y gestión de gastos y compras
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setOcrDialogOpen(true)}>
              <Scan className="w-4 h-4 mr-2" />
              Escanear ticket
            </Button>
            <Button onClick={openNewDialog}>
              <Plus className="w-4 h-4 mr-2" />
              Nuevo gasto
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Receipt className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-sm text-muted-foreground">Total gastos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Euro className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{formatCurrency(stats.thisMonth)}</p>
                  <p className="text-sm text-muted-foreground">Este mes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-100 rounded-lg">
                  <Wallet className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{formatCurrency(stats.pending)}</p>
                  <p className="text-sm text-muted-foreground">Pendiente pago</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Camera className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.ocrCount}</p>
                  <p className="text-sm text-muted-foreground">Procesados OCR</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px] relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por proveedor, concepto..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pending">Pendiente</SelectItem>
                  <SelectItem value="paid">Pagado</SelectItem>
                  <SelectItem value="partial">Parcial</SelectItem>
                </SelectContent>
              </Select>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Categoría" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {Object.entries(EXPENSE_CATEGORY_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="month"
                className="w-44"
                value={monthFilter}
                onChange={(e) => setMonthFilter(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Proveedor</TableHead>
                <TableHead>Concepto</TableHead>
                <TableHead>Categoría</TableHead>
                <TableHead className="text-right">Base</TableHead>
                <TableHead className="text-right">IVA</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    Cargando gastos...
                  </TableCell>
                </TableRow>
              ) : filteredExpenses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    <Wallet className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground mb-4">No hay gastos registrados</p>
                    <Button onClick={openNewDialog}>
                      <Plus className="w-4 h-4 mr-2" />
                      Añadir gasto
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                filteredExpenses.map((expense) => (
                  <TableRow key={expense.id}>
                    <TableCell className="font-mono text-sm">
                      {format(new Date(expense.date), "dd/MM/yyyy")}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{expense.supplier_name}</p>
                        {expense.supplier_tax_id && (
                          <p className="text-xs text-muted-foreground">{expense.supplier_tax_id}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="truncate max-w-[200px]">{expense.concept}</p>
                        {expense.invoice_number && (
                          <p className="text-xs text-muted-foreground">Nº {expense.invoice_number}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {expense.category && (
                        <Badge variant="outline">
                          {EXPENSE_CATEGORY_LABELS[expense.category] || expense.category}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(expense.base_amount)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-muted-foreground">
                      {expense.tax_rate}%
                    </TableCell>
                    <TableCell className="text-right font-mono font-medium">
                      {formatCurrency(expense.total_amount)}
                    </TableCell>
                    <TableCell>
                      <Badge className={EXPENSE_STATUS_COLORS[expense.payment_status]}>
                        {EXPENSE_STATUS_LABELS[expense.payment_status]}
                      </Badge>
                      {expense.ocr_processed && (
                        <Badge variant="outline" className="ml-1 text-xs">
                          OCR
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {expense.payment_status === 'pending' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Marcar como pagado"
                            onClick={() => markPaidMutation.mutate(expense)}
                          >
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(expense)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            if (confirm('¿Eliminar este gasto?')) {
                              deleteMutation.mutate(expense.id);
                            }
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>

        {/* Expense Form Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingId ? 'Editar gasto' : 'Nuevo gasto'}
              </DialogTitle>
              <DialogDescription>
                {editingId ? 'Modifica los datos del gasto' : 'Registra un nuevo gasto o compra'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fecha *</Label>
                  <Input
                    type="date"
                    value={editingExpense.date}
                    onChange={(e) => setEditingExpense({
                      ...editingExpense,
                      date: e.target.value
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Categoría</Label>
                  <Select
                    value={editingExpense.category || 'other'}
                    onValueChange={(v) => setEditingExpense({
                      ...editingExpense,
                      category: v as ExpenseCategory
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(EXPENSE_CATEGORY_LABELS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Proveedor *</Label>
                <Select
                  value={editingExpense.supplier_id || '__sporadic__'}
                  onValueChange={(val) => {
                    if (val === '__sporadic__') {
                      setEditingExpense({
                        ...editingExpense,
                        supplier_id: '',
                        supplier_name: '',
                        supplier_tax_id: ''
                      });
                    } else {
                      const supplier = suppliers.find((s: any) => s.id === val);
                      if (supplier) {
                        setEditingExpense({
                          ...editingExpense,
                          supplier_id: val,
                          supplier_name: supplier.legal_name,
                          supplier_tax_id: supplier.tax_id || ''
                        });
                      }
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar proveedor..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__sporadic__">Proveedor esporadico (nuevo)</SelectItem>
                    {suppliers.map((s: any) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.legal_name} {s.tax_id ? `- ${s.tax_id}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {!editingExpense.supplier_id && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nombre proveedor *</Label>
                    <Input
                      value={editingExpense.supplier_name}
                      onChange={(e) => setEditingExpense({
                        ...editingExpense,
                        supplier_name: e.target.value
                      })}
                      placeholder="Nombre del proveedor"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>NIF/CIF Proveedor</Label>
                    <Input
                      value={editingExpense.supplier_tax_id || ''}
                      onChange={(e) => setEditingExpense({
                        ...editingExpense,
                        supplier_tax_id: e.target.value.toUpperCase()
                      })}
                      placeholder="B12345678"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Concepto *</Label>
                <Input
                  value={editingExpense.concept}
                  onChange={(e) => setEditingExpense({
                    ...editingExpense,
                    concept: e.target.value
                  })}
                  placeholder="Descripción del gasto"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Base imponible (€) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editingExpense.base_amount}
                    onChange={(e) => setEditingExpense({
                      ...editingExpense,
                      base_amount: parseFloat(e.target.value) || 0
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>IVA (%)</Label>
                  <Select
                    value={String(editingExpense.tax_rate)}
                    onValueChange={(v) => setEditingExpense({
                      ...editingExpense,
                      tax_rate: parseFloat(v)
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0%</SelectItem>
                      <SelectItem value="4">4%</SelectItem>
                      <SelectItem value="10">10%</SelectItem>
                      <SelectItem value="21">21%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Total</Label>
                  <div className="h-10 px-3 py-2 border rounded-md bg-muted font-mono">
                    {formatCurrency(
                      editingExpense.base_amount * (1 + editingExpense.tax_rate / 100)
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nº Factura proveedor</Label>
                  <Input
                    value={editingExpense.invoice_number || ''}
                    onChange={(e) => setEditingExpense({
                      ...editingExpense,
                      invoice_number: e.target.value
                    })}
                    placeholder="FA-2024-001"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Estado de pago</Label>
                  <Select
                    value={editingExpense.payment_status}
                    onValueChange={(v) => setEditingExpense({
                      ...editingExpense,
                      payment_status: v as ExpensePaymentStatus
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pendiente</SelectItem>
                      <SelectItem value="paid">Pagado</SelectItem>
                      <SelectItem value="partial">Parcial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notas</Label>
                <Textarea
                  value={editingExpense.notes || ''}
                  onChange={(e) => setEditingExpense({
                    ...editingExpense,
                    notes: e.target.value
                  })}
                  placeholder="Observaciones..."
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? 'Guardando...' : editingId ? 'Guardar' : 'Crear'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* OCR Dialog */}
        <Dialog open={ocrDialogOpen} onOpenChange={setOcrDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Scan className="w-5 h-5" />
                Escanear Ticket o Factura
              </DialogTitle>
              <DialogDescription>
                Sube una imagen o PDF y extraeremos los datos automáticamente
              </DialogDescription>
            </DialogHeader>
            <OCRProcessor onResult={handleOCRResult} onCancel={() => setOcrDialogOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
